import { produce } from 'immer';
import type { FormsState } from './types';

export const initialState: FormsState = {
  bankDetails: {
    index: 0,
    type: 'report',
    sectionHeading: 'The bank involved',
    isComplete: false,
    isCurrent: true,
    bank: {
      label:
        'I am raising concerns about wrongdoing involving the following bank:',
      value: '',
    },
  },
  relationDetails: {
    index: 1,
    type: 'report',
    sectionHeading: 'Your relationship to this bank',
    isComplete: false,
    isCurrent: false,
    bankRelationship: {
      label: 'I am or was previously:',
      value: '',
    },
  },
  concernDetails: {
    index: 2,
    type: 'report',
    sectionHeading: 'Your concerns',
    isComplete: false,
    isCurrent: false,
    concern: {
      label: 'Tell us about your concerns',
      value: '',
    },
  },
  documentsDetails: {
    index: 3,
    type: 'report',
    sectionHeading: 'Relevant documents (optional)',
    isComplete: false,
    isCurrent: false,
    documents: {
      label: 'Documents',
      value: [],
    },
  },
  expectationDetails: {
    index: 4,
    type: 'report',
    sectionHeading: 'Your expectations',
    isComplete: false,
    isCurrent: false,
    expectation: {
      label:
        'What would you like to happen as a result of raising your concerns?',
      value: '',
    },
  },
  personalDetails: {
    index: 5,
    type: 'report',
    sectionHeading: 'Your personal information',
    isComplete: false,
    isCurrent: false,
    personalPrivacy: {
      label: 'Please select who can receive your personal information:',
      value: '',
    },
    firstName: {
      label: 'First name',
      value: '',
    },
    lastName: {
      label: 'Last name',
      value: '',
    },
    emailAddress: {
      label: 'Email address',
      value: '',
    },
    phoneNumber: {
      label: 'Phone',
      value: '',
    },
    region: {
      label: 'Your region',
      value: '',
    },
    contactPreference: {
      label: 'Please select your preferred contact method:',
      value: '',
    },
    preferences: {
      label: 'Please specify any communication needs or preferences',
      value: '',
    },
  },
  agreementDetails: {
    index: 6,
    type: 'report',
    sectionHeading: 'Agreement to share your concerns',
    isComplete: false,
    isCurrent: false,
    privacyAgreement: {
      value: '',
    },
  },
  declarationDetails: {
    index: 7,
    type: 'report',
    sectionHeading: 'Declarations',
    isComplete: false,
    isCurrent: false,
    confirm: {
      label:
        'I believe on reasonable grounds that the information I am providing is true or likely to be true.',
      value: false,
    },
    understand1: {
      label:
        'I can contact the Service at any time if I believe I am being intimidated, victimised or treated unfairly as a result of raising these concerns.',
      value: false,
    },
  },
  representativeDetails: {
    index: 0,
    type: 'complaint',
    sectionHeading: 'Representative',
    onBehalf: {
      label:
        'Are you making this complaint on behalf of yourself or someone else?',
      value: '',
    },
    repPersonalTitle: {
      label: 'Title',
      value: '',
    },
    repPronouns: {
      label: 'Pronouns',
      value: '',
    },
    repPronounsOther: {
      label: 'Self described pronouns',
      value: '',
    },
    repFirstName: {
      label: 'First name',
      value: '',
    },
    repLastName: {
      label: 'Last name',
      value: '',
    },
    repRelationship: {
      label: 'Your relationship to the complainant',
      value: '',
    },
    repOrganisation: {
      label: 'Organisation (optional)',
      value: '',
    },
    repEmail: {
      label: 'Email',
      value: '',
    },
    repPhone: {
      label: 'Phone',
      value: '',
    },
    preferences: {
      label: 'Do you have any communication needs or preferences?',
      value: '',
    },
    isComplete: false,
    isCurrent: true,
  },
  contactDetails: {
    index: 1,
    type: 'complaint',
    sectionHeading: 'Contact details',
    title: {
      label: 'Title',
      value: '',
    },
    firstName: {
      label: 'First name',
      value: '',
    },
    lastName: {
      label: 'Last name',
      value: '',
    },
    pronouns: {
      label: 'Pronouns',
      value: '',
    },
    pronounsOther: {
      label: 'Self described pronouns',
      value: '',
    },
    emailAddress: {
      label: 'Email address',
      value: '',
    },
    phoneNumber: {
      label: 'Phone number',
      value: '',
    },
    organisation: {
      label: 'Organisation (optional)',
      value: '',
    },
    region: {
      label: 'Region',
      value: '',
    },
    preferences: {
      label: 'Your communication needs or preferences',
      value: '',
    },
    isComplete: false,
    isCurrent: false,
  },
  yourComplaint: {
    index: 2,
    type: 'complaint',
    sectionHeading: 'Your complaint details',
    bank: {
      label: 'Which bank is your complaint about?',
      value: '',
    },
    complaint: {
      label: 'Tell us about your complaint',
      value: '',
    },
    isComplete: false,
    isCurrent: false,
  },
  yourExpectations: {
    index: 3,
    type: 'complaint',
    sectionHeading: 'What would you like to happen?',
    expectation: {
      label: 'Your expected outcome',
      value: '',
    },
    isComplete: false,
    isCurrent: false,
  },
  relevantDocuments: {
    index: 4,
    type: 'complaint',
    sectionHeading: 'Relevant documents',
    documents: {
      label: 'Documents',
      value: [],
    },
    isComplete: false,
    isCurrent: false,
  },
  privacyAuthorisation: {
    index: 5,
    type: 'complaint',
    sectionHeading: 'Privacy agreement',
    privacyAgreement: {
      label: 'Privacy agreement type',
      value: '',
    },
    isComplete: false,
    isCurrent: false,
  },
  demographicInformation: {
    index: 6,
    type: 'complaint',
    sectionHeading: 'About you',
    isComplete: false,
    isCurrent: false,
    demographicAge: {
      label: 'Age',
      value: '',
    },
    demographicGender: {
      label: 'Gender',
      value: '',
    },
    demographicGenderOther: {
      label: 'Self described gender',
      value: '',
    },
    demographicEthnicity: {
      label: 'Ethnicity',
      value: [],
      conditionalInputValue: [],
    },
    demographicFindOutReferral: {
      label: 'How did you find out about us?',
      value: '',
    },
    demographicFindOutReferralDetail: {
      label: '',
      value: '',
    },
  },
  submit: {
    index: 7,
    type: 'complaint',
    sectionHeading: 'Submit',
    isComplete: false,
    isCurrent: false,
  },
  contactUs: {
    index: 0,
    type: 'contact',
    sectionHeading: 'Contact us',
    firstName: {
      label: 'First name',
      value: '',
    },
    lastName: {
      label: 'Last name',
      value: '',
    },
    emailAddress: {
      label: 'Email address',
      value: '',
    },
    region: {
      label: 'Region',
      value: '',
    },
    isComplete: false,
    isCurrent: true,
    isHidden: true,
  },
  currentForm: {},
  unfinishedStep: 0,
  currentIndex: 0,
  currentStep: 0,
  showPreview: false,
};

export default function formsReducer(
  state = initialState,
  action: any = {
    type: '',
    payload: {},
  }
) {
  switch (action.type) {
    case 'RESET_STORE': {
      return initialState;
    }
    case 'persist/REHYDRATE': {
      const incoming = action.payload;

      return produce(state, (draft) => {
        // handle incoming state
        if (incoming && incoming.forms) {
          Object.keys(incoming.forms).forEach((form) => {
            if (typeof draft[form] !== 'undefined') {
              draft[form] = incoming.forms[form];
            }
          });

          draft.currentForm = draft[incoming.currentForm];
          draft.currentIndex = incoming.currentIndex;
          draft.currentStep = incoming.currentStep;
          draft.unfinishedStep = incoming.unfinishedStep;
          draft.showPreview = incoming.showPreview;
        }
      });
    }

    case 'FORM_FIELD_UPDATE': {
      const nextState = produce(state, (draft) => {
        if (typeof draft[action.form] === 'undefined') {
          return draft;
        }

        if (typeof draft[action.form][action.name] === 'object') {
          draft[action.form][action.name].value = action.value;
        } else {
          draft[action.form][action.name] = action;
        }

        draft.showPreview = false;
      });

      return nextState;
    }

    case 'FORM_FIELD_CONDITIONAL_INPUT_UPDATE': {
      const nextState = produce(state, (draft) => {
        draft[action.form][action.name].conditionalInputValue = action.value;
        draft.showPreview = false;
      });

      return nextState;
    }

    case 'FORM_EDIT_STEP': {
      const nextState = produce(state, (draft) => {
        Object.keys(draft).forEach((form) => {
          // Only process objects that have a 'type' property
          if (
            draft[form] &&
            typeof draft[form] === 'object' &&
            draft[form].type &&
            draft[form].type === action.formType
          ) {
            const index = draft[form].index;

            if (draft[form].isCurrent) {
              draft[form].isCurrent = false;
            }

            // open selected step
            if (index === action.index) {
              draft[form].isCurrent = true;
              draft.currentIndex = index;
              draft.currentForm = draft[form];
            }
          }
        });

        draft.showPreview = false;
      });

      return nextState;
    }

    case 'FORM_SUBMIT_STEP': {
      const nextState = produce(state, (draft) => {
        const formNames = Object.keys(draft);
        let nextNotCompleteStep = 1;
        formNames.forEach((form) => {
          console.log('form', form);
          console.log('draft[form]', draft);
          // Only process objects that have a 'type' property
          if (
            draft[form] &&
            typeof draft[form] === 'object' &&
            draft[form].type &&
            draft[form].type === draft[action.form].type
          ) {
            // set previous steps closed
            if (draft[form].index === action.index) {
              draft[form].isCurrent = false;
              draft[form].isComplete = true;
            }
            // look for next not complete step
            if (draft[form].index === action.index + nextNotCompleteStep) {
              if (!draft[form].isComplete) {
                draft.currentIndex = draft[form].index;
                draft.currentForm = draft[form];
                draft[form].isCurrent = true;
              } else {
                nextNotCompleteStep += 1;
              }
            }
          }
        });
      });

      return nextState;
    }
    case 'FORM_NEXT': {
      const nextState = produce(state, (draft) => {
        const nextStateForms = Object.keys(draft);
        const nextFormNotCompleteStep = 1;
        const nextStateIndex = draft.currentIndex + 1;
        nextStateForms.forEach((form) => {
          // Only process objects that have a 'type' property
          if (
            draft[form] &&
            typeof draft[form] === 'object' &&
            draft[form].type &&
            draft[form].type === draft[action.form].type
          ) {
            // set previous steps closed
            if (draft[form].index === action.index) {
              draft[form].isCurrent = false;
              draft[form].isComplete = true;
              draft[form].isValid = true;
            }
            // look for next not complete step
            if (draft[form].index === action.index + nextFormNotCompleteStep) {
              draft[form].isCurrent = true;
              draft.currentIndex = nextStateIndex;
              draft.currentForm = draft[form];

              // unfinished state should only be greater than the previous set one
              if (nextStateIndex > draft.unfinishedStep) {
                draft.unfinishedStep = nextStateIndex;
              }
            }
          }
        });
      });

      return nextState;
    }

    case 'FORM_BACK': {
      const nextState = produce(state, (draft) => {
        const newIndex = draft.currentIndex - 1;
        const allForms = Object.keys(draft);
        allForms.forEach((form) => {
          // Only process objects that have a 'type' property
          if (
            draft[form] &&
            typeof draft[form] === 'object' &&
            draft[form].type &&
            draft[form].type === draft[action.form].type
          ) {
            // set previous steps closed
            if (draft[form].index === action.index) {
              draft[form].isCurrent = false;
            }
            // look for next not complete step
            if (draft[form].index === action.index - 1) {
              draft[form].isCurrent = true;
              draft.currentIndex = newIndex;
              draft.currentForm = draft[form];
            }
          }
        });

        draft.showPreview = false;
      });

      return nextState;
    }

    case 'FORM_ADD_DOCUMENT': {
      const nextState = produce(state, (draft) => {
        draft[action.form][action.name].value = action.documents;
      });

      return nextState;
    }
    case 'FORM_REMOVE_DOCUMENT': {
      const nextState = produce(state, (draft) => {
        const removeDocs = draft[action.form].documents.value.slice();
        removeDocs.splice(action.index, 1);

        draft[action.form].documents.value = removeDocs;
      });

      return nextState;
    }

    case 'FORM_SUBMIT_RESPONSE':
      // fix issue with existing state not fully clearing
      return [];

    case 'FORM_SHOW_PREVIEW': {
      const nextState = produce(state, (draft) => {
        draft.showPreview = true;
      });

      return nextState;
    }
    default:
      return state;
  }
}
